import { BaseModel } from "./BaseModel";
import { ColumnHeaders, TabNames } from '../modules/Constants';


export class PrototypeModel extends BaseModel {
    constructor() {
        super();
        this.TabName = TabNames.PROTOTYPE;
        this.TabIndex = 8;
        this.DataProcessor = new PrototypeProcessor();
    }
}

export class PrototypeProcessor {
    ExchangeRates;
    Years;
    Currency;
    Records;
    Headers;

    process(data) {

        this.ExchangeRates = [];
        this.Years = [];
        this.Records = [];
        this.Headers = [];
        this.Currency = data.currency;

        let study = data.study;
        let snapshots = data.snapshots;
        let version = data.version;


        study.exchangeRates.forEach(rate => {
            rate.Item2.Values.forEach(item => {
                this.ExchangeRates.push({
                    currency: rate.Item1,
                    year: new Date(item.Date).getUTCFullYear(),
                    value: item.Value
                });
            });
        });


        let firstSOPYear = Math.min.apply(Math, snapshots.map(snapshot => snapshot.SaleableParts?.map(part => new Date(part?.CustomerSOP?.Value.Fields[0]).getUTCFullYear())).reduce(function (a, b) { return a.concat(b) }, []));
        let firstYearSpend = firstSOPYear - (version?.SpendYearsPriorToSOP?.Value?.Fields[0] ?? 2);
        let lastYearSpend = firstSOPYear + (version?.SpendYearsAfterSOP?.Value?.Fields[0] ?? 0);
        snapshots.forEach(snapshot => {
            snapshot.SaleableParts.forEach(part => {
                let endDate = part.CustomerVolume?.Value?.Fields[0]?.EndDate;
                if (endDate) {
                    let year = new Date(endDate).getUTCFullYear();
                    if (year > lastYearSpend) {
                        lastYearSpend = year;
                    }
                }
            });
        })

        var yearsSpending = Array.from(Array((lastYearSpend - firstYearSpend) + 1), (_, x) => x + firstYearSpend);
        this.Years = yearsSpending;


        //JG - Update Years in case there are additional years outside of Spending Years.
        snapshots.forEach(snapshot => {
            snapshot?.Prototypes.forEach(prototype => {
                let volumes = prototype?.Value?.Fields[0]?.Volume?.Value?.Fields[0]?.Values;

                volumes.forEach(volume => {
                    let year = new Date(volume.Date).getUTCFullYear();

                    if (this.Years.findIndex(x => x === year) <= -1) {
                        this.Years.push(year);
                    }
                });
            });
        });


        this.Years.sort();

        snapshots.forEach(snapshot => {
            this.setRecords(snapshot);
        });

        this.configureHeader();
        let result = {
            headers: this.Headers,
            display: this.getDisplay(),
            numerics: this.getNumerics()
        };


        return result;
    }

    setRecords(snapshot) {
        snapshot?.Prototypes.forEach(prototype => {
            this.Records.push(new PrototypeRowModel(this.Years, snapshot, prototype, this.ExchangeRates, this.Currency));
        });
    }

    configureHeader() {
        let header = [
            ColumnHeaders.OPERATION_NUMBER,
            ColumnHeaders.PROTOTYPE_DESCRIPTION,
            ColumnHeaders.UNIT_COST,
            `${ColumnHeaders.UNIT_COST} ${ColumnHeaders.CURRENCY_PREFIX}`
        ]

        this.Years.forEach(year => {
            header.push(`${year} ${ColumnHeaders.UNIT_COST} ${ColumnHeaders.CONVERTED_PREFIX}`);
        });

        header.push(
            `${ColumnHeaders.UNIT_COST} ${ColumnHeaders.CURRENCY_PREFIX} ${ColumnHeaders.CONVERTED_PREFIX}`,
            ColumnHeaders.UNIT_PRICE,
            `${ColumnHeaders.UNIT_PRICE} ${ColumnHeaders.CURRENCY_PREFIX}`
        );

        this.Years.forEach(year => {
            header.push(`${ year } ${ ColumnHeaders.UNIT_PRICE } ${ ColumnHeaders.CONVERTED_PREFIX }`);
        });

        header.push(`${ColumnHeaders.UNIT_PRICE} ${ColumnHeaders.CURRENCY_PREFIX} ${ColumnHeaders.CONVERTED_PREFIX}`)

        this.Years.forEach(year => {
            header.push(year + " Volume");
        });

        header.push(
            ColumnHeaders.START_YEAR
        );

        this.Headers = header;
    }

    getDisplay() {
        let display = [];

        display.push(this.Headers);

        this.Records.forEach(record => {
            let row = [
                record.OptionName,
                record.PrototypeDescription,
                record.UnitCostAmount,
                record.UnitCostCurrency,
            ];

            record.UnitCost.forEach(volume => {
                row.push(volume);
            });

            row.push(
                this.Currency,
                record.UnitPriceAmount,
                record.UnitPriceCurrency
            );

            record.UnitPrice.forEach(volume => {
                row.push(volume);
            });

            row.push(this.Currency);

            record.Volume.forEach(volume => {
                row.push(volume);
            });

            row.push(this.Years[0]);

            display.push(row);
        });

        return display;
    }

    getNumerics() {
        let numeric = [];

        //set number formatting
        let startUnitCost = 4;
        let endUnitCost = this.Years.length + startUnitCost - 1;

        let startUnitPrice = endUnitCost + 4;
        let endUnitPrice = this.Years.length + startUnitPrice - 1;

        let startVolume = endUnitPrice + 2;
        let endVolume = this.Years.length + startVolume - 1;


        numeric.push({ start: startUnitCost, end: endUnitCost });
        numeric.push({ start: startUnitPrice, end: endUnitPrice });
        numeric.push({ start: startVolume, end: endVolume });

        return numeric;
    }

}

export class PrototypeRowModel {
    OptionName = "";
    PrototypeDescription = "";
    UnitCostAmount = "";
    UnitCostCurrency = "";
    UnitPriceAmount = "";
    UnitPriceCurrency = "";
    Volume = [];
    UnitCost = [];
    UnitPrice = [];

    constructor(years, snapshot, prototype, exchangeRates, exchangeCurrency) {
        this.OptionName = snapshot.GroupName;
        this.PrototypeDescription = prototype?.Value?.Fields[0]?.Description?.Value?.Fields[0];
        this.UnitCostAmount = prototype?.Value?.Fields[0]?.Cost?.Value?.Fields[0];
        this.UnitCostCurrency = prototype?.Value?.Fields[0]?.CostCurrency?.Value?.Fields[0];
        this.UnitPriceAmount = prototype?.Value?.Fields[0]?.Price?.Value?.Fields[0];
        this.UnitPriceCurrency = prototype?.Value?.Fields[0]?.PriceCurrency?.Value?.Fields[0];

        this.Volume = [];
        this.UnitCost = [];
        this.UnitPrice = [];

        years.forEach(year => {
            let volume = undefined;
            let convUnitCost = undefined;
            let convUnitPrice = undefined;

            if (prototype) {
                volume = prototype?.Value?.Fields[0]?.Volume?.Value?.Fields[0]?.Values.find(item => new Date(item.Date).getUTCFullYear() === year)?.Value ?? 0;

                let exchangeRate = exchangeRates?.find(rate => rate.year === year && rate.currency === exchangeCurrency);
                let currentUnitCostRate = exchangeRates?.find(rate => rate.year === year && rate.currency === this.UnitCostCurrency);
                let currentUnitPriceRate = exchangeRates?.find(rate => rate.year === year && rate.currency === this.UnitPriceCurrency);

                if (exchangeRate !== undefined && currentUnitCostRate !== undefined) {
                    convUnitCost = volume * this.UnitCostAmount * exchangeRate.value / currentUnitCostRate.value;
                }

                if (exchangeRate !== undefined && currentUnitPriceRate !== undefined) {
                    convUnitPrice = volume * this.UnitPriceAmount * exchangeRate.value / currentUnitPriceRate.value;
                }
            }

            this.Volume.push(volume);
            this.UnitPrice.push(convUnitPrice);
            this.UnitCost.push(convUnitCost);
        });

    }
}
